import { useT } from '@hana/i18n';
import { Table, type Column } from '@hana/ui';
import { CCF, DDL, DOC, JOB, LS, MON, MS, OB, SCY, TR, TRF, fi } from './data';
import { Card } from './panels';
import s from './bod.module.css';

// Mint-BOD · tab "Hiệu suất dịch vụ" · nhóm "Thời gian" (Mint-BOD-SvcTime): biểu đồ job theo loại, bảng theo tháng,
// ma trận dịch vụ con × tháng (ô tô đậm theo khối lượng trong hàng) và hai ô Nhận định tính từ số liệu.
const SUBS: readonly (readonly [string, string, readonly number[]])[] = [['TRF', 'Vận tải', TRF], ['DOC', 'Chứng từ', DOC], ['CCF', 'Thông quan', CCF], ['SCY', 'Bãi / CY', SCY], ['DDL', 'Giao nhận chứng từ', DDL]];
const TM: readonly (readonly [string, readonly number[]])[] = [['Job', JOB], ['OB', OB], ['LS', LS], ['TR', TR], ['TRF', TRF], ['DOC', DOC], ['CCF', CCF], ['SCY', SCY], ['DDL', DDL]];
const CW = 720, CH = 180, SW = 60;
const avg3 = (a: readonly number[], e: number) => (a[e]! + a[e - 1]! + a[e - 2]!) / 3;
const q = (a: readonly number[], e: number) => a[e]! + a[e - 1]! + a[e - 2]!;
const sg = (v: number) => (v >= 0 ? '+' : '−') + Math.abs(v).toFixed(0) + '%';
const chg = (c: number, p: number) => sg(p ? ((c - p) / p) * 100 : 0);
const maxAt = (a: readonly number[]) => a.indexOf(Math.max(...a));
const minAt = (a: readonly number[]) => a.indexOf(Math.min(...a));

type MR = { key: string; c: string; nm: string; a: readonly number[] };

export function SvcTime() {
  const t = useT();
  const mx = Math.max(...OB, ...LS, ...TR) * 1.1;
  const y = (v: number) => CH - (Math.max(0, v) / mx) * (CH - 12);
  const bw = (SW * 0.6) / 3;
  const bars = (a: readonly number[], j: number) => a.map((v, i) => { const h = (Math.max(0, v) / mx) * (CH - 12); return h > 0.2 ? `M${(i * SW + SW * 0.2 + j * bw).toFixed(1)} ${(CH - h).toFixed(1)}h${(bw - 2).toFixed(1)}v${h.toFixed(1)}h-${(bw - 2).toFixed(1)}z ` : ''; }).join('');
  const line = TRF.map((v, i) => `${i ? 'L' : 'M'}${(i * SW + SW / 2).toFixed(1)} ${y(v).toFixed(1)}`).join(' ');
  const r3 = avg3(JOB, 11), p3 = avg3(JOB, 8);
  const ins1 = [
    t('bod.svct.i1', { hi: MS[maxAt(JOB)]!, hn: fi(Math.max(...JOB)), lo: MS[minAt(JOB)]!, ln: fi(Math.min(...JOB)) }),
    t('bod.svct.i2', { r: fi(r3), c: chg(r3, p3) }) + (r3 >= p3 ? t('bod.svct.i2b') : '.'),
    t('bod.svct.i3', { a: chg(avg3(TRF, 11), avg3(TRF, 8)), b: chg(avg3(DOC, 11), avg3(DOC, 8)) }),
  ];
  const qd = SUBS.map(([c, nm, a]) => ({ c, nm: nm.toLowerCase(), d: q(a, 8) ? ((q(a, 11) - q(a, 8)) / q(a, 8)) * 100 : 0 }));
  const up = qd.reduce((a, b) => (b.d > a.d ? b : a)), dn = qd.reduce((a, b) => (b.d < a.d ? b : a));
  const ins2 = [
    t(up.d >= 0 ? 'bod.svct.j1up' : 'bod.svct.j1dn', { c: up.c, n: up.nm, d: sg(up.d) }),
    t('bod.svct.j2', { c: dn.c, n: dn.nm, d: sg(dn.d) }),
    t('bod.svct.j3'),
  ];
  const mcols: Column<{ key: string; i: number }>[] = [
    { key: 'm', header: t('bod.svct.month'), width: '13%', cell: (r) => <b>{MON[r.i]}</b> },
    ...TM.map(([k, a], j) => ({ key: k, header: k, width: '9.6%', align: 'right' as const, cell: (r: { i: number }) => (j === 0 ? <b>{fi(a[r.i]!)}</b> : fi(a[r.i]!)) })),
  ];
  const mrows = MS.map((m, i) => ({ key: m, i })).reverse();
  const sumM = MS.map((_, i) => SUBS.reduce((x, r) => x + r[2][i]!, 0));
  const heat = (v: number, m: number) => ({ background: `color-mix(in srgb, var(--pri) ${Math.round(5 + 40 * (m ? v / m : 0))}%, transparent)` });
  const xrows: MR[] = [...SUBS.map(([c, nm, a]) => ({ key: c, c, nm, a })), { key: 'sum', c: t('bod.svct.sum'), nm: '', a: sumM }];
  const xcols: Column<MR>[] = [
    { key: 'c', header: t('bod.svc.g.sub'), width: '15%', cell: (r) => <><b>{r.c}</b> <small>{r.nm}</small></> },
    ...MS.map((m, i) => ({ key: m, header: m, width: '5.7%', align: 'right' as const, cell: (r: MR) => (r.key === 'sum' ? fi(r.a[i]!) : <span style={heat(r.a[i]!, Math.max(...r.a))}>{fi(r.a[i]!)}</span>) })),
    { key: 'tot', header: t('bod.svct.total'), width: '7%', align: 'right', cell: (r) => <b>{fi(r.a.reduce((x, v) => x + v, 0))}</b> },
    { key: 'q', header: t('bod.svct.quarter'), width: '6.6%', align: 'right', cell: (r) => (r.key === 'sum' ? '' : <b className={q(r.a, 11) >= q(r.a, 8) ? s.up : s.down}>{chg(q(r.a, 11), q(r.a, 8))}</b>) },
  ];
  return (
    <>
      <Card title={t('bod.svct.k1')} sub={t('bod.svct.s1')} span={8}>
        <div className={s.leg}><span><i className={s.fOb} />OB</span><span><i className={s.fLs} />LS</span><span><i className={s.fTr} />TR</span><span><i className={`${s.ln} ${s.cWarn}`} />{t('bod.svct.trf')}</span><em>{t('bod.svct.peak', { n: fi(Math.max(...OB)) })}</em></div>
        <svg role="img" aria-label={`${t('bod.svct.k1')}: ${MS.map((m, i) => `${m} OB ${OB[i]} · LS ${LS[i]} · TR ${TR[i]}`).join(', ')}`} width="100%" height="180" viewBox={`0 0 ${CW} ${CH}`} preserveAspectRatio="none">
          <path d={[0.25, 0.5, 0.75, 1].map((f) => `M0 ${(CH - f * (CH - 12)).toFixed(1)}H${CW}`).join(' ') + ` M0 ${CH - 0.5}H${CW}`} className={s.gridL} vectorEffect="non-scaling-stroke" />
          <path d={bars(OB, 0)} className={s.fOb} /><path d={bars(LS, 1)} className={s.fLs} /><path d={bars(TR, 2)} className={s.fTr} />
          <path d={line} fill="none" stroke="var(--warn)" strokeWidth="2" vectorEffect="non-scaling-stroke" />
        </svg>
        <div className={s.xl} aria-hidden="true">{MS.map((k) => <span key={k}>{k}</span>)}</div>
      </Card>
      <Card title={t('bod.svc.ins')} sub={t('bod.svc.ins.sub')} span={4}><ul className={s.ins}>{ins1.map((x) => <li key={x}>{x}</li>)}</ul></Card>
      <Card title={t('bod.svct.k2')} sub={t('bod.svct.s2')} span={12}>
        <Table label={t('bod.svct.k2')} columns={mcols} rows={mrows} rowKey={(r) => r.key} dense />
      </Card>
      <Card title={t('bod.svct.k3')} sub={t('bod.svct.s3')} span={12}>
        <Table label={t('bod.svct.k3')} columns={xcols} rows={xrows} rowKey={(r) => r.key} dense />
      </Card>
      <Card title={t('bod.svc.ins')} sub={t('bod.svct.ins2')} span={12}><ul className={s.ins}>{ins2.map((x) => <li key={x}>{x}</li>)}</ul></Card>
    </>
  );
}
